/**
 * Refined Design — a configurator vase in the cart, handled as one thing.
 *
 * rd-cart-build.liquid draws the vase as one row over its hidden lines. This makes that
 * row work: its quantity steps every line of the vase together (each by what one vase
 * takes of it), its remove takes them all out, and its picture is the snapshot of the
 * 3D vase the configurator kept in this browser when it was added.
 *
 * Updates go through /cart/update.js with the cart sections asked back, and announce
 * themselves with the theme's own CartUpdateEvent — so the drawer, the page and the
 * cart bubble all redraw exactly as they do for any other cart change.
 */
import { CartUpdateEvent } from '@theme/events';

const PICTURE = 'rd-build-img:';

/**
 * The parts of a row, as [line keys, quantity per vase] — one part per variant. A part
 * can be spread over several lines: Shopify splits a line where a discount counts only
 * some of its units.
 */
function partsOf(row) {
  return (row.dataset.rdLines || '')
    .split(',')
    .filter(Boolean)
    .map((part) => {
      const at = part.lastIndexOf(':');
      const keys = part.slice(0, at).split(';').filter(Boolean);
      return [keys, Math.max(1, Number(part.slice(at + 1)) || 1)];
    })
    .filter(([keys]) => keys.length);
}

async function setVases(row, count) {
  // The whole quantity of each part goes on its first line, and its other lines are
  // emptied; Shopify splits it again by itself if a discount still calls for it.
  const updates = {};
  partsOf(row).forEach(([keys, per]) => {
    keys.forEach((key, i) => (updates[key] = i === 0 ? per * count : 0));
  });
  if (!Object.keys(updates).length) return;

  const sections = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) sections.add(el.dataset.sectionId);
  });

  row.classList.add('is-updating');
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const res = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        sections: Array.from(sections).join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await res.json();
    if (!res.ok || data.errors) throw new Error(data.description || data.errors || 'cart');
    // The count a shopper sees — a vase as one — as the cart section printed it.
    let itemCount = data.item_count;
    const html = data.sections && Object.values(data.sections)[0];
    if (html) {
      const found = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]');
      if (found) itemCount = parseInt(found.textContent, 10) || 0;
    }
    row.dispatchEvent(
      new CartUpdateEvent(data, 'rd-cart-build', {
        itemCount,
        source: 'rd-cart-build',
        sections: data.sections,
      })
    );
  } catch (e) {
    row.classList.remove('is-updating');
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const row = target.closest('[data-rd-build]');
  if (!row) return;
  const count = Number(row.dataset.rdCount) || 1;
  const step = target.closest('[data-rd-build-step]');
  if (step) {
    event.preventDefault();
    setVases(row, Math.max(0, count + Number(step.dataset.rdBuildStep)));
    return;
  }
  if (target.closest('[data-rd-build-remove]')) {
    event.preventDefault();
    setVases(row, 0);
  }
});

/**
 * The vase's own picture, where this browser has it. Applied again whenever the cart is
 * redrawn — a redraw puts the fallback photo back.
 */
function showPictures() {
  document.querySelectorAll('img[data-rd-build-img]').forEach((img) => {
    let src = null;
    try {
      src = window.localStorage.getItem(PICTURE + img.dataset.rdBuildImg);
    } catch (e) {
      return;
    }
    if (!src || img.getAttribute('src') === src) return;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.setAttribute('src', src);
    img.classList.add('is-snapshot');
  });
}

showPictures();
new MutationObserver(showPictures).observe(document.documentElement, {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: ['src'],
});
